import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Menu, PowerOff, Home } from 'lucide-react';
import logo from '../../logo.png';
import Sidebar from './Sidebar';
import { MENUS_POR_ROL } from './menus';

/**
 * Layout reutilizable para todas las páginas internas del sistema.
 * Incluye:
 * - Header guinda con logo, botón "Inicio" y avatar
 * - Botón hamburguesa que abre el sidebar desde CUALQUIER módulo
 * - Sidebar con menú agrupado por categorías según el rol del usuario
 *
 * Uso:
 *   <Layout>
 *     ...contenido del módulo...
 *   </Layout>
 */
export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const rol = user?.rol || 'promotor';
  const navItems = MENUS_POR_ROL[rol] || MENUS_POR_ROL['promotor'];
  const esInicio = location.pathname === '/dashboard';

  const handleNav = (path) => {
    setSidebarOpen(false);
    navigate(path);
  };

  return (
    <div className="min-h-screen bg-ink-50 flex flex-col">
      {/* Header */}
      <header className="bg-guinda-900 text-white px-3 md:px-4 py-2.5 md:py-3 flex items-center justify-between shadow-lg sticky top-0 z-40">
        <div className="flex items-center gap-3 md:gap-4">
          <button onClick={() => setSidebarOpen(true)}
            title="Abrir menú"
            className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition">
            <Menu size={20} />
          </button>
          <img src={logo} alt="DOMINIUM"
            className="h-9 md:h-11 object-contain drop-shadow-[0_2px_10px_rgba(255,255,255,0.5)]" />
        </div>

        <div className="flex items-center gap-2 md:gap-3">
          {/* Botón Inicio (solo si NO estamos en el dashboard) */}
          {!esInicio && (
            <button onClick={() => navigate('/dashboard')}
              title="Ir al inicio"
              className="hidden sm:flex items-center gap-1.5 bg-white/10 hover:bg-white/20 px-3 py-2 rounded-lg text-xs font-medium transition">
              <Home size={14} />
              Inicio
            </button>
          )}

          <div className="text-right hidden sm:block">
            <p className="font-semibold text-xs md:text-sm leading-tight">{user?.nombre} {user?.apellido_paterno}</p>
            <p className="text-guinda-200 text-[10px] md:text-xs capitalize">{rol.replace(/_/g, ' ')}</p>
          </div>

          <div className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-gradient-to-br from-guinda-500 to-guinda-800 ring-2 ring-white/20 flex items-center justify-center text-white font-bold text-xs md:text-sm">
            {user?.nombre?.[0]?.toUpperCase() || 'U'}
          </div>

          <button onClick={logout} title="Cerrar sesión"
            className="bg-white/10 hover:bg-white/20 p-2 rounded-lg transition">
            <PowerOff size={16} />
          </button>
        </div>
      </header>

      {/* Sidebar */}
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        user={user}
        navItems={navItems}
        rol={rol}
        location={location}
        onNav={handleNav}
        onLogout={logout}
      />

      {/* Contenido */}
      <main className="flex-1">
        {children}
      </main>
    </div>
  );
}
